import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ImageUploader } from './ImageUploader';
import { JsonEditor } from './JsonEditor';
import { getSectionTitle, getFieldMeta } from './field-labels';
import type {
  ContenidoPublico,
  PaginaPublica,
  TipoContenidoPublico,
} from '../../types/contenido-publico';

interface ContenidoPublicoFormProps {
  pagina: PaginaPublica;
  initialData?: ContenidoPublico;
  onSubmit: (data: {
    pagina: PaginaPublica;
    seccion: string;
    clave: string;
    tipo: TipoContenidoPublico;
    valor: string;
    orden: number;
    activo: boolean;
    metadata?: string;
  }) => void;
  onCancel: () => void;
}

const TIPOS: { value: TipoContenidoPublico; label: string }[] = [
  { value: 'TEXTO', label: 'Texto corto' },
  { value: 'TEXTO_LARGO', label: 'Texto largo' },
  { value: 'IMAGEN', label: 'Imagen' },
  { value: 'ENLACE', label: 'Enlace' },
  { value: 'JSON', label: 'Lista / JSON' },
];

export const ContenidoPublicoForm: React.FC<ContenidoPublicoFormProps> = ({
  pagina,
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [seccion, setSeccion] = useState(initialData?.seccion ?? '');
  const [clave, setClave] = useState(initialData?.clave ?? '');
  const [tipo, setTipo] = useState<TipoContenidoPublico>(initialData?.tipo ?? 'TEXTO');
  const [valor, setValor] = useState(initialData?.valor ?? '');
  const [orden, setOrden] = useState(initialData?.orden ?? 0);
  const [activo, setActivo] = useState(initialData?.activo ?? true);
  const [metadata, setMetadata] = useState(
    initialData?.metadata ? JSON.stringify(initialData.metadata, null, 2) : '',
  );

  useEffect(() => {
    if (!initialData) {
      setSeccion('');
      setClave('');
      setTipo('TEXTO');
      setValor('');
      setOrden(0);
      setActivo(true);
      setMetadata('');
    }
  }, [initialData, pagina]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      pagina,
      seccion: seccion.trim(),
      clave: clave.trim(),
      tipo,
      valor: valor.trim(),
      orden,
      activo,
      metadata: metadata.trim() || undefined,
    });
  };

  const meta = getFieldMeta(pagina, seccion, clave);

  const renderValorInput = () => {
    switch (tipo) {
      case 'TEXTO_LARGO':
        return (
          <textarea
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Ingrese el valor"
          />
        );
      case 'IMAGEN':
        return <ImageUploader value={valor} onChange={setValor} />;
      case 'JSON':
        return <JsonEditor value={valor} onChange={setValor} />;
      default:
        return (
          <input
            type="text"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Ingrese el valor"
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xs font-medium text-gray-900">
            {initialData ? 'Editar contenido' : 'Nuevo contenido'}
          </h3>
          {initialData && (
            <p className="text-xs text-gray-500 mt-1">
              {getSectionTitle(seccion)} → {meta.label}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Sección</label>
          <input
            type="text"
            value={seccion}
            onChange={(e) => setSeccion(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="ej: hero"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Clave</label>
          <input
            type="text"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="ej: titulo"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoContenidoPublico)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Orden</label>
          <input
            type="number"
            value={orden}
            onChange={(e) => setOrden(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
            />
            <span className="text-xs font-medium text-gray-700">Activo</span>
          </label>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Valor
          {meta.hint && <span className="text-gray-400 font-normal ml-2">— {meta.hint}</span>}
        </label>
        {renderValorInput()}
      </div>

      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Metadata (JSON opcional)
        </label>
        <textarea
          value={metadata}
          onChange={(e) => setMetadata(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-xs"
          placeholder='{"alt": "Descripción de imagen"}'
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700"
        >
          {initialData ? 'Actualizar' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};
